import {
	IListAttrs,
	IListColumnConfig,
	IListOperations,
} from '@datachain/ui-sdk/common';
import { immerable, produce } from 'immer';
import { Map, OrderedSet, Set } from 'immutable';

import { PublicationStatus } from '../../domain/publication-status';
import { ExpositionEntity } from '../exposition-view/exposition.entity';
import { ExpositionItemEntity } from './exposition-item.entity';

export interface IExpositionListExtra
	extends IListColumnConfig<ExpositionItemEntity> {
	width?: string | number;
	filterValue?: Array<string | number>;
	filterValues?: Array<string | number>;
	sortIndex?: number;
	sortOrder?: 'asc' | 'desc';
}

interface IExpositionListEntity extends IListAttrs<ExpositionItemEntity> {
	availableColumns: Map<string, IExpositionListExtra>;
}

const entityDefaults: IExpositionListEntity = {
	elements: Map(),
	availableColumns: Map(),
};

export class ExpositionListEntity
	implements
		IExpositionListEntity,
		IListOperations<ExpositionListEntity, ExpositionItemEntity>
{
	private [immerable] = true;
	public elements = entityDefaults.elements;
	public availableColumns = entityDefaults.availableColumns;

	public static build(
		availableCols: Array<IExpositionListExtra>
	): ExpositionListEntity {
		const inst = new ExpositionListEntity();
		const cols = availableCols.reduce((acc, curr) => {
			acc.push([curr.field, curr]);
			return acc;
		}, new Array<[string, IExpositionListExtra]>());
		inst.availableColumns = Map(cols);
		return inst;
	}

	private constructor() {}

	public getAvailableColumns(): Map<
		string,
		IListColumnConfig<ExpositionItemEntity>
	> {
		return this.availableColumns;
	}

	public setElements(
		elems: Map<string, ExpositionItemEntity>
	): ExpositionListEntity {
		return produce(this, (draft: ExpositionListEntity) => {
			draft.elements = elems;
		});
	}

	public setApiStatusRunningOnItem(
		expositionId: number,
		value: boolean
	): ExpositionListEntity {
		const selectedElement = this.elements.get(`${expositionId}`);
		if (!selectedElement) {
			return this.setElements(this.elements);
		}

		const updatedElement = selectedElement.setApiStatusRunning(value);

		const updatedElements = this.elements.set(
			`${expositionId}`,
			updatedElement
		);

		return this.setElements(updatedElements);
	}

	public setActionInProgressOnItem(
		expositionId: number,
		value: boolean
	): ExpositionListEntity {
		const selectedElement = this.elements.get(`${expositionId}`);
		if (!selectedElement) {
			return this.setElements(this.elements);
		}

		const updatedElement = selectedElement.setActionInProgress(value);

		const updatedElements = this.elements.set(
			`${expositionId}`,
			updatedElement
		);

		return this.setElements(updatedElements);
	}

	public updateItem(
		expositionToUpdate: ExpositionItemEntity
	): ExpositionListEntity {
		const updatedElements = this.elements.set(
			`${expositionToUpdate.id}`,
			expositionToUpdate
		);

		return this.setElements(updatedElements);
	}

	public resetRunningActionOn(id: number): ExpositionListEntity {
		return produce(this, (draft: ExpositionListEntity) => {
			const maybeFound = draft.elements.get(`${id}`);
			if (!maybeFound) {
				return;
			}
			draft.elements = draft.elements.set(
				`${id}`,
				maybeFound.resetRunningAction()
			);
		});
	}

	public updateSelected(ids: Array<string>): ExpositionListEntity {
		return produce(this, (draft: ExpositionListEntity) => {
			draft.elements = ids.reduce((acc, curr) => {
				const item = acc.get(`${curr}`);
				if (item) {
					acc = acc.set(`${curr}`, item.toggleSelection());
					return acc;
				}
				return acc;
			}, this.elements);
		});
	}

	public extractUniqTags(...startValues: Array<string>): Set<string> {
		const allTags = this.elements
			.reduce(
				(acc, expositionItem) =>
					Set([...acc.toArray(), ...expositionItem.tagsArr]),
				Set<string>()
			)
			.sort((a, b) => a.localeCompare(b));

		return OrderedSet<string>([...startValues, ...allTags]);
	}

	public extractModifiers(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, expositionItem) => {
			if (
				expositionItem.lastElemModificationBy &&
				expositionItem.lastElemModificationBy.name
			) {
				acc = expositionItem.lastElemModificationBy.name.includes(
					'undefined undefined'
				)
					? acc
					: acc.add(expositionItem.lastElemModificationBy.name);
			}
			return acc;
		}, Set<string>(startValues));
	}

	public extractActionCreators(...startValues: Array<string>): Set<string> {
		return this.elements.reduce((acc, expositionItem) => {
			if (
				expositionItem.lastExpositionActionBy &&
				expositionItem.lastExpositionActionBy.name
			) {
				acc = expositionItem.lastExpositionActionBy.name.includes(
					'undefined undefined'
				)
					? acc
					: acc.add(expositionItem.lastExpositionActionBy.name);
			}
			return acc;
		}, Set<string>(startValues));
	}

	public extractStatus(): Set<PublicationStatus> {
		return this.elements.reduce(
			(acc, expositionItem) => acc.add(expositionItem.apiStatus),
			Set<PublicationStatus>()
		);
	}

	public setDetails(view: ExpositionEntity): ExpositionListEntity {
		const target = this.elements.get(`${view.id}`);
		if (!target) {
			return this;
		}
		return produce(this, (draft: ExpositionListEntity) => {
			const updated = target.updateView(view);
			this.elements.set(`${view.id}`, updated);
			draft.elements = this.elements.set(`${view.id}`, updated);
		});
	}
}
